<template>
	<v-menu v-model="menu" :close-on-content-click="false" offset="1" transition="scale-transition" location="bottom" min-width="350" max-width="350">
		<template v-slot:activator="{ props: menuProps }">
			<v-text-field
				v-bind="menuProps"
				:model-value="displayText"
				:placeholder="computedPlaceholder"
				:label="props.label"
				variant="underlined"
				readonly
				append-inner-icon="mdi-menu-down"
				:disabled="disabled"
				hide-details
				density="compact"
				@click:clear="clearSelection"
			>
			</v-text-field>
		</template>

		<v-card class="elevation-8 border">
			<div v-if="showTabs" class="pa-3 d-flex flex-wrap" style="gap: 8px">
				<v-chip
					v-for="tab in availableTabs"
					:key="tab.value"
					size="small"
					:color="activeTab === tab.value ? 'teal-darken-4' : ''"
					:variant="activeTab === tab.value ? 'flat' : 'tonal'"
					@click="activeTab = tab.value"
				>
					{{ tab.label }} ({{ getCount(tab.value) }})
				</v-chip>
			</div>

			<v-divider v-if="showTabs"></v-divider>

			<div class="pt-2 px-5 pb-3">
				<v-text-field
					v-model="searchQuery"
					:placeholder="`${t(`search_for_${searchPlaceholder}`)}...`"
					variant="outlined"
					hide-details
					prepend-inner-icon="mdi-magnify"
					autofocus
				></v-text-field>
			</div>

			<v-list height="300" class="overflow-y-auto pa-0">
				<v-list-item v-for="item in filteredItems" :key="item._id" :active="item._id === modelValue" color="teal-lighten-5" @click="selectItem(item)">
					<v-list-item-title class="text-body-2">
						{{ item[labelKey] }}
						<slot name="item-label-append" :item="item"></slot>
					</v-list-item-title>

					<template v-slot:append v-if="item._id === modelValue">
						<v-icon color="teal-darken-4" size="small">mdi-check</v-icon>
					</template>
				</v-list-item>

				<v-list-item v-if="filteredItems.length === 0">
					<v-list-item-title class="text-grey text-center py-4"> No {{ searchPlaceholder }} found </v-list-item-title>
				</v-list-item>
			</v-list>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface ListItem {
	_id: string | number;
	type?: string;
	[key: string]: any;
}

interface GroupedItems {
	unallocated?: ListItem[];
	reserve?: ListItem[];
	all?: ListItem[];
	[key: string]: ListItem[] | undefined;
}

const props = withDefaults(
	defineProps<{
		modelValue: string | number | null;
		items: GroupedItems;
		labelKey?: string;
		searchPlaceholder?: string;
		placeholder?: string;
		showTabs?: boolean;
		disabled?: boolean;
		label?: string;
	}>(),
	{
		labelKey: 'displayName',
		searchPlaceholder: 'items',
		showTabs: true,
		disabled: false,
	}
);

const emit = defineEmits(['update:modelValue']);

const menu = ref(false);
const searchQuery = ref('');
const activeTab = ref('all');

const computedPlaceholder = computed(() => {
	return props.placeholder ?? `${t(`select_${props.searchPlaceholder}`)}`;
});

const normalizedItems = computed(() => {
	return Object.entries(props.items || {}).flatMap(([type, items]) =>
		(items || []).map((item) => ({
			...item,
			type,
		}))
	);
});

const displayText = computed(() => {
	const item = normalizedItems.value.find((i) => i._id === props.modelValue);

	return item ? item[props.labelKey] : '';
});

const availableTabs = computed(() => {
	const tabs = [{ label: t('all'), value: 'all' }];

	if ((props.items?.reserve || []).length) {
		tabs.push({ label: t('duty_dayType_reserve'), value: 'reserve' });
	}

	if ((props.items?.unallocated || []).length) {
		tabs.push({ label: t('unallocated'), value: 'unallocated' });
	}

	return tabs;
});

const getCount = (tabValue: string) => {
	if (tabValue === 'all') {
		return normalizedItems.value.length;
	}

	return normalizedItems.value.filter((i) => i.type === tabValue).length;
};

const filteredItems = computed(() => {
	return normalizedItems.value.filter((item) => {
		const label = String(item[props.labelKey] || '').toLowerCase();

		const matchesSearch = label.includes(searchQuery.value.toLowerCase());

		const matchesTab = item.type === activeTab.value;

		return matchesSearch && matchesTab;
	});
});

const selectItem = (item: ListItem) => {
	emit('update:modelValue', item._id);
	menu.value = false;
};

const clearSelection = () => {
	emit('update:modelValue', null);
};
</script>
